import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import {
    htmlSourcesHaveRemoteImages,
    readRemoteImagesSession,
    writeRemoteImagesSession,
} from '../../utils/remoteImages';

interface RemoteImagesContextValue {
    showRemoteImages: boolean;
    hasRemoteImages: boolean;
    revealRemoteImages: () => void;
}

const RemoteImagesContext = React.createContext<RemoteImagesContextValue>({
    showRemoteImages: false,
    hasRemoteImages: false,
    revealRemoteImages: () => undefined,
});

interface RemoteImagesProviderProps {
    changeId?: string | null;
    htmlSources: Array<string | null | undefined>;
    children: React.ReactNode;
}

/**
 * Card-scoped, session-only consent to load remote intake images.
 */
export function RemoteImagesProvider(props: RemoteImagesProviderProps) {
    const { changeId, htmlSources, children } = props;
    const hasRemoteImages = React.useMemo(
        () => htmlSourcesHaveRemoteImages(htmlSources),
        [htmlSources]
    );
    const [showRemoteImages, setShowRemoteImages] = React.useState(
        () => readRemoteImagesSession(changeId)
    );

    React.useEffect(() => {
        setShowRemoteImages(readRemoteImagesSession(changeId));
    }, [changeId]);

    const revealRemoteImages = React.useCallback(() => {
        writeRemoteImagesSession(changeId, true);
        setShowRemoteImages(true);
    }, [changeId]);

    const value = React.useMemo(
        () => ({ showRemoteImages, hasRemoteImages, revealRemoteImages }),
        [showRemoteImages, hasRemoteImages, revealRemoteImages]
    );

    return (
        <RemoteImagesContext.Provider value={value}>
            {children}
        </RemoteImagesContext.Provider>
    );
}

export function useRemoteImages(): RemoteImagesContextValue {
    return React.useContext(RemoteImagesContext);
}

/**
 * Button shown only when the card has remote-image placeholders and images are still blocked.
 */
export function ShowRemoteImagesButton({ className }: { className?: string }) {
    const { t } = useTranslation();
    const { hasRemoteImages, showRemoteImages, revealRemoteImages } = useRemoteImages();

    if (!hasRemoteImages || showRemoteImages)
        return null;

    return (
        <div className={className}>
            <button
                type="button"
                className="inline-flex items-center text-xs font-normal text-gray-600 hover:text-gray-900 dark:text-gray-100 hover:underline bg-transparent border border-gray-200 dark:border-gray-700 rounded px-2 py-1"
                onClick={revealRemoteImages}
            >
                <Icon type="eye" className="inline mr-1" size={3} />
                {t('changes.show_remote_images') || 'Show images'}
            </button>
        </div>
    );
}
